import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from "antd";
import {
  ApartmentOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTable } from "@refinedev/antd";
import { useCreate, useUpdate, useDelete } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "../../../hooks";
import { PageHeader } from "../../../components/common/PageHeader";

interface AuditCompany {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  member_count: number;
  created: string;
  modified: string;
}

interface FormValues {
  name: string;
  is_active: boolean;
}

export const AuditCompanyAdminList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  usePageTitle(t("admin_audit_companies.title"));

  const { tableProps, tableQuery } = useTable<AuditCompany>({
    resource: "v1/admin/audit-companies",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "name", order: "asc" }] },
    syncWithLocation: false,
  });

  const { mutateAsync: createMutate } = useCreate();
  const { mutateAsync: updateMutate } = useUpdate();
  const { mutateAsync: deleteMutate } = useDelete();

  const [editing, setEditing] = useState<AuditCompany | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ name: "", is_active: true });
    setOpen(true);
  };

  const openEdit = (record: AuditCompany) => {
    setEditing(record);
    form.setFieldsValue({ name: record.name, is_active: record.is_active });
    setOpen(true);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await updateMutate({
          resource: "v1/admin/audit-companies",
          id: editing.id,
          values,
        });
        message.success(t("admin_audit_companies.update_success"));
      } else {
        await createMutate({
          resource: "v1/admin/audit-companies",
          values,
        });
        message.success(t("admin_audit_companies.create_success"));
      }
      setOpen(false);
      tableQuery?.refetch?.();
    } catch (e: any) {
      if (e?.errorFields) return; // form validation
      message.error(e?.message || "Error");
    }
  };

  const onDelete = async (record: AuditCompany) => {
    try {
      await deleteMutate({
        resource: "v1/admin/audit-companies",
        id: record.id,
      });
      message.success(t("admin_audit_companies.delete_success"));
      tableQuery?.refetch?.();
    } catch (e: any) {
      message.error(e?.message || "Error");
    }
  };

  return (
    <Card>
      <PageHeader
        icon={<ApartmentOutlined />}
        accent="#13C2C2"
        title={t("admin_audit_companies.title")}
        subtitle={t("admin_audit_companies.subtitle")}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t("admin_audit_companies.create")}
          </Button>
        }
      />
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Table
          {...tableProps}
          rowKey="id"
          columns={[
            {
              title: t("admin_audit_companies.name"),
              dataIndex: "name",
              key: "name",
            },
            {
              title: t("admin_audit_companies.slug"),
              dataIndex: "slug",
              key: "slug",
            },
            {
              title: t("admin_audit_companies.members"),
              dataIndex: "member_count",
              key: "member_count",
              width: 110,
              align: "right" as const,
            },
            {
              title: t("admin_audit_companies.active"),
              dataIndex: "is_active",
              key: "is_active",
              width: 110,
              render: (v: boolean) =>
                v ? (
                  <Tag color="green">{t("admin_audit_companies.active")}</Tag>
                ) : (
                  <Tag>{t("admin_audit_companies.inactive")}</Tag>
                ),
            },
            {
              title: t("common.actions"),
              key: "actions",
              width: 190,
              render: (_: unknown, record: AuditCompany) => (
                <Space>
                  <Button
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => navigate(`/admin/audit-companies/${record.id}`)}
                  >
                    {t("admin_audit_companies.manage", "Gestionar")}
                  </Button>
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => openEdit(record)}
                  />
                  <Popconfirm
                    title={t("admin_audit_companies.delete_confirm")}
                    okText={t("common.confirm")}
                    cancelText={t("common.cancel")}
                    onConfirm={() => onDelete(record)}
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                    />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        title={
          editing
            ? t("admin_audit_companies.edit")
            : t("admin_audit_companies.create")
        }
        open={open}
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label={t("admin_audit_companies.name")}
            name="name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("admin_audit_companies.active")}
            name="is_active"
            valuePropName="checked"
          >
            <Switch
              checkedChildren={t("common.yes")}
              unCheckedChildren={t("common.no")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AuditCompanyAdminList;
